import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import {
  CHART_OPTIONS_COACHING_EFFECTIVENESS_EVENT_WISE,
  DEFAULT_DARK_THEME_BORDER_COLOR,
  DEFAULT_DARK_THEME_TEXT_COLOR,
  DEFAULT_FONT_FAMILY,
  DEFAULT_LIGHT_THEME_BORDER_COLOR,
  DEFAULT_LIGHT_THEME_TEXT_COLOR,
  EVENTS_CONFIG,
} from '@app-core/constants/constants';
import { EventWiseCoachingEffectiveness } from '@app-reports/models/reports.model';
import { TranslateService } from '@ngx-translate/core';
import { Chart, ChartConfiguration } from 'chart.js/auto';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-coaching-effectiveness-event-wise',
  templateUrl: './coaching-effectiveness-event-wise.component.html',
  styleUrls: ['./coaching-effectiveness-event-wise.component.scss'],
})
export class CoachingEffectivenessEventWiseComponent implements OnChanges, OnDestroy {
  @Input() public loader: boolean = true;
  @Input() public currentTheme: string;
  @Input() public eventWiseCoachingEffectiveness: EventWiseCoachingEffectiveness[];

  public showChart: boolean;
  public chart: Chart;

  private unsubscribeOnChanges: Subject<void> = new Subject<void>();
  private unsubscribeOnDestroy: Subject<void> = new Subject<void>();

  constructor(public translate: TranslateService, private elementRef: ElementRef) {
    this.translate.onLangChange.subscribe(() => {
      if (!this.loader) {
        this.generateChart();
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.currentTheme) {
      this.changeTheme();
    } else {
      if (!this.loader) {
        this.generateChart();
      }
    }
  }

  private generateChart() {
    if (!this.eventWiseCoachingEffectiveness || !this.eventWiseCoachingEffectiveness.length) {
      this.showChart = false;
      return;
    }

    if (this.chart) {
      this.chart.destroy();
    }

    const ctx: any = this.elementRef.nativeElement.querySelector(`#coachingEffectivenessEventWiseChart`);

    const labels = this.eventWiseCoachingEffectiveness.map((event) => {
      const config = EVENTS_CONFIG[event.eventType];
      const subEventType = event?.originalEventType || null;
      return subEventType
        ? `${this.translate.instant(config.label)} (${this.translate.instant(config.childConfigs[subEventType].shortLabel)})`
        : config
        ? this.translate.instant(config.label)
        : event.eventType;
    });

    const beforeCoachingData = this.eventWiseCoachingEffectiveness.map((event) => event.beforeCoaching);
    const afterCoachingData = this.eventWiseCoachingEffectiveness.map((event) => event.afterCoaching);
    const fleetAverageData = this.eventWiseCoachingEffectiveness.map((event) => event.fleetAverage);

    const chartConfig: ChartConfiguration = {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            type: 'line',
            label: 'Fleet Average',
            data: fleetAverageData,
            borderColor: '#37A2EB',
            backgroundColor: '#37A2EB',
            borderDash: [5, 2],
            fill: false,
          },
          {
            type: 'bar',
            label: 'Before Coaching',
            data: beforeCoachingData,
            backgroundColor: '#E6DCFB',
            borderColor: '#E6DCFB',
            borderWidth: 0,
            barPercentage: 0.5,
            barThickness: 20,
            categoryPercentage: 0.5,
            stack: 'combined',
            borderRadius: 6,
          },
          {
            type: 'bar',
            label: 'After Coaching',
            data: afterCoachingData,
            backgroundColor: '#9A66FF',
            borderColor: '#9A66FF',
            borderWidth: 1,
            barThickness: 20,
            barPercentage: 0,
            categoryPercentage: 0.5,
            minBarLength: 1,
            borderRadius: 6,
          },
        ],
      },
      options: {
        ...CHART_OPTIONS_COACHING_EFFECTIVENESS_EVENT_WISE,
        plugins: {
          ...CHART_OPTIONS_COACHING_EFFECTIVENESS_EVENT_WISE.plugins,
          tooltip: {
            usePointStyle: true,
            bodyFont: {
              family: DEFAULT_FONT_FAMILY,
            },
            callbacks: {
              label: function (context) {
                const value = context.raw;
                return ` ${value}`;
              },
            },
          },
        },
      },
    };
    this.chart = new Chart(ctx, chartConfig);
    this.assignAxisTexts();
    this.chart.update();
    this.changeTheme();
    this.showChart = true;
    this.loader = false;
  }

  private assignAxisTexts() {
    const yAxixText = this.translate.instant('coachingEffectivenessEventWiseYaxisTitle');

    this.chart.options.scales.y['title'] = {
      ...this.chart.options.scales.y['title'],
      text: `${yAxixText.toUpperCase()}`,
    };

    const xAxisText = this.translate.instant('coachingEffectivenessEventWiseXaxisTitle');
    this.chart.options.scales.x['title'] = {
      ...this.chart.options.scales.x['title'],
      text: `${xAxisText}`,
    };
  }

  public changeTheme() {
    if (this.chart) {
      if (this.currentTheme === 'light') {
        this.chart.options.plugins.legend.labels.color = DEFAULT_LIGHT_THEME_TEXT_COLOR;
        this.chart.options.scales.y.grid.color = DEFAULT_LIGHT_THEME_BORDER_COLOR;
        this.chart.options.scales.x.ticks.color = DEFAULT_LIGHT_THEME_TEXT_COLOR;
        this.chart.options.scales.y.ticks.color = DEFAULT_LIGHT_THEME_TEXT_COLOR;
        this.chart.options.scales.y['title'].color = DEFAULT_LIGHT_THEME_TEXT_COLOR;
        this.chart.options.scales.x['title'].color = DEFAULT_LIGHT_THEME_TEXT_COLOR;
      } else {
        this.chart.options.plugins.legend.labels.color = DEFAULT_DARK_THEME_TEXT_COLOR;
        this.chart.options.scales.y.grid.color = DEFAULT_DARK_THEME_BORDER_COLOR;
        this.chart.options.scales.x.ticks.color = DEFAULT_DARK_THEME_TEXT_COLOR;
        this.chart.options.scales.y.ticks.color = DEFAULT_DARK_THEME_TEXT_COLOR;
        this.chart.options.scales.y['title'].color = DEFAULT_DARK_THEME_TEXT_COLOR;
        this.chart.options.scales.x['title'].color = DEFAULT_DARK_THEME_TEXT_COLOR;
      }
      this.chart.update();
    }
  }

  public ngOnDestroy() {
    this.unsubscribeOnChanges.next();
    this.unsubscribeOnChanges.complete();

    this.unsubscribeOnDestroy.next();
    this.unsubscribeOnDestroy.complete();
  }
}
